import { computed, inject, Injectable } from '@angular/core';
import { ShieldApiStore, ShieldZoneRequest } from '@bn/app-shield';

@Injectable({
  providedIn: 'root',
})
export class WafService {
  #shieldStore = inject(ShieldApiStore);

  public existingDisabledRules = computed(() => this.#shieldStore.selectedEntity()()?.wafDisabledRules || []);
  public existingLogOnlyRules = computed(() => this.#shieldStore.selectedEntity()()?.wafLogOnlyRules || []);
  public selectedShieldId = computed(() => this.#shieldStore.selectedEntity()()?.shieldZoneId || -1);

  public enableRules(ruleIds: string[]) {
    this.saveChanges(
      {
        wafDisabledRules: WafService.removeItems(this.existingDisabledRules(), ruleIds),
        wafLogOnlyRules: WafService.removeItems(this.existingLogOnlyRules(), ruleIds),
      },
      this.createCustomFields(ruleIds, 'batchEnable'),
    );
  }

  public disableRules(ruleIds: string[]) {
    this.saveChanges(
      {
        wafDisabledRules: WafService.addItems(this.existingDisabledRules(), ruleIds),
        wafLogOnlyRules: WafService.removeItems(this.existingLogOnlyRules(), ruleIds),
      },
      this.createCustomFields(ruleIds, 'batchDisable'),
    );
  }

  public logRules(ruleIds: string[]) {
    this.saveChanges(
      {
        wafDisabledRules: WafService.removeItems(this.existingDisabledRules(), ruleIds),
        wafLogOnlyRules: WafService.addItems(this.existingLogOnlyRules(), ruleIds),
      },
      this.createCustomFields(ruleIds, 'batchLog'),
    );
  }

  public isRuleBlocking(ruleId: string) {
    return ![...this.existingDisabledRules(), ...this.existingLogOnlyRules()].includes(ruleId);
  }

  private saveChanges(data: Partial<ShieldZoneRequest>, customFields: string[]) {
    this.#shieldStore.updateShield({
      id: Number(this.selectedShieldId()),
      data,
      customFields,
    });
  }

  private createCustomFields(ruleIds: string[], batchActionName: string): string[] {
    return [...ruleIds.map((ruleId) => `rule-${ruleId}`), ...(!ruleIds.at(0) ? [batchActionName] : [])];
  }

  /**
   * Merges two arrays of string identifiers, eliminates duplicates, and filters out undefined values.
   *
   * @param currentIds - The array of current identifiers which may include undefined values.
   * @param selectedIds - The array of newly selected identifiers which may include undefined values.
   * @return A new array containing unique, defined identifiers.
   */
  static addItems<T>(currentIds: T[], selectedIds: T[]): T[] {
    return Array.from(new Set([...currentIds, ...selectedIds].filter((id) => id !== undefined)));
  }

  /**
   * Removes items from the provided array if they exist.
   *
   * @param targetArray - The array from which items should be removed.
   * @param itemsToRemove - The array of items to be removed.
   * @returns A new array with the items removed.
   */
  static removeItems<T>(targetArray: T[], itemsToRemove: T[]): T[] {
    return targetArray.filter((item) => !itemsToRemove.includes(item));
  }
}
